// Ported from genieos/web/src/components/historian/MultiAxisChart.tsx @ 84923d036
/**
 * MultiAxisChart - Multi-axis time-series chart for historian data
 *
 * Renders up to 10 variables across 3 Y-axes using the native ECharts wrapper
 * (EChartsMultiLineChart). The axis ids left / right / right2 name UNIT GROUPS;
 * the edge each group is drawn on comes from layoutYAxes (djin-cloud#829), and
 * the chips are labelled by that edge. Supports zoom via the ECharts
 * dataZoom slider, a rich custom tooltip, and legend toggles.
 *
 * Y-AXIS SCALING (Demsv1.0#2795). This chart used to leave the wrapper on its
 * zero-baseline default, so a 59.92-60.10 Hz grid-frequency trace was drawn on
 * a 0-70 axis and read as a dead-flat line. Each axis now carries its own mode:
 *
 *   auto   - bounds are supplied to ECharts as FUNCTIONS of the visible extent.
 *            dataZoom defaults to filterMode 'filter', so the extent shrinks
 *            with the zoom window and the axis re-fits live while dragging,
 *            with no React re-render in the loop.
 *   zero   - no bounds supplied; the wrapper's scale:false forces a zero
 *            baseline, which is the right reading for power and current.
 *   manual - operator-pinned numbers.
 *
 * The span badge above the chart is not decoration: auto-scaling changes the
 * chart's vertical gain invisibly, and without a stated span a 0.2 Hz noise
 * band and a 5 Hz excursion look the same. Badge and axis both derive from
 * computeAutoBounds over the same extent, so they cannot disagree.
 *
 * NOTE: this component originally used 'recharts', which rendered an empty plot
 * at runtime under prod Vite/Rollup (ESM circular-dep crash, worked around by a
 * compatibility shim whose cartesian sub-components were no-op stubs). recharts
 * has since been removed entirely (#1042); this uses the native ECharts wrapper.
 */

import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, RefreshCw, BarChart3 } from 'lucide-react';
import { useHistorian } from '@/contexts/HistorianContext';
import {
  EChartsMultiLineChart,
  type MultiLineSeriesConfig,
  type AxisBoundValue,
  type YAxisId,
  type ReferenceLineConfig,
} from '@/components/charts/echarts/EChartsMultiLineChart';
import { layoutYAxes } from '@/components/charts/echarts/yAxisLayout';
import { Button, Card } from '@/components/gamma';
import { YAxisScaleChip } from './YAxisScaleChip';
import {
  computeAutoBounds,
  extentOf,
  formatAxisSpan,
  nominalFor,
} from '@/utils/axisScaling';

/** The order the unit groups are listed, and claim chart edges, in. */
const AXIS_ORDER: YAxisId[] = ['left', 'right', 'right2'];

/**
 * The label each chip shows, by the SLOT its axis is drawn in, not by the
 * group's logical id. The wrapper places occupied groups left, right, then
 * outer right (djin-cloud#829), so a kW group whose id is `right` is drawn on
 * the left edge when it is the only group. A chip reading "Right" there would
 * send the operator to the wrong side of the chart.
 */
const SLOT_LABEL = ['Left', 'Right', 'Right 2'] as const;

/**
 * Settling delay before recomputing the span badge from a zoom.
 *
 * The AXIS itself re-fits continuously (ECharts evaluates the bound functions
 * during the drag), so this debounce only paces the badge text. Keeping React
 * out of the drag is what stops a 21k-point chart from restarting its render
 * on every animation frame.
 */
const ZOOM_SETTLE_MS = 150;

interface MultiAxisChartProps {
  height?: number;
  className?: string;
}

/** Escape dynamic strings before injecting into the ECharts tooltip HTML. */
const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string)
  );

/** The parts of an ECharts tooltip param the value read depends on. */
interface TooltipValueParam {
  value?: unknown;
  encode?: { y?: unknown };
}

/**
 * The Y reading carried by one tooltip param, or null when there is none.
 *
 * The wrapper feeds every series as `[timestamp, value]` pairs, so ECharts
 * hands the formatter `value: [ts, v]`, not a bare number. Printing that
 * pair is how the tooltip came to read "1789648440000,0 kW" (djin-cloud#829).
 * Measured against echarts 6.1.0: a param carries `value: [ts, v]`,
 * `encode: { x: [0], y: [1] }` and `dimensionNames: ['x', 'y']`, and a gap
 * arrives as `[ts, null]`. So the Y column comes from `encode.y`, falling back
 * to index 1 of the pair; a bare number is still accepted in case the
 * encoding ever changes.
 *
 * A gap or a non-finite value is not a reading and returns null. A real 0 IS
 * a reading, so nothing here may use a falsy check.
 */
function tooltipY(p: TooltipValueParam): number | null {
  let y: unknown = p.value;
  if (Array.isArray(p.value)) {
    const yDims = p.encode?.y;
    const yIndex = Array.isArray(yDims) && typeof yDims[0] === 'number' ? yDims[0] : 1;
    y = p.value[yIndex];
  }
  return typeof y === 'number' && Number.isFinite(y) ? y : null;
}

export const MultiAxisChart: React.FC<MultiAxisChartProps> = ({
  height = 400,
  className,
}) => {
  const { state, executeQuery, setYAxisScale } = useHistorian();
  const {
    selectedVariables,
    queryResult,
    queryLoading,
    queryError,
    timeRange,
    yAxisScales,
  } = state;

  /**
   * Index window currently shown, as [start, end] inclusive. null means the
   * whole loaded range. Updated on a settled dataZoom so the badge reports what
   * is actually on screen rather than what was originally queried.
   */
  const [zoomWindow, setZoomWindow] = useState<[number, number] | null>(null);

  /**
   * The live zoom, as ECharts percentages, in a ref rather than state.
   *
   * A ref because the chart must NOT re-render while the operator drags — the
   * axis re-fits inside ECharts on its own. It is read when the chart element
   * is rebuilt for some other reason (a scale-mode change), and handed back so
   * that rebuild does not throw the zoom away: `notMerge` makes every rebuild a
   * full replace, and Manual mode exists precisely to pin bounds on a region
   * the operator has just zoomed into (#2795).
   */
  const zoomPctRef = useRef<{ start: number; end: number } | null>(null);

  // Transform query result data for chart - add timestamp as number
  const chartData = useMemo(() => {
    if (!queryResult?.data) return [];

    return queryResult.data.map((row) => {
      const transformed: Record<string, number | null> = {
        timestamp:
          typeof row.timestamp === 'number'
            ? row.timestamp
            : new Date(row.timestamp as unknown as string).getTime(),
      };

      // Add all selected variable values
      selectedVariables.forEach((v) => {
        const value = row[v.alias];
        transformed[v.alias] =
          typeof value === 'number' ? value : value != null ? Number(value) : null;
      });

      return transformed;
    });
  }, [queryResult?.data, selectedVariables]);

  // Map selected variables to the wrapper's series config (dataKey = alias).
  //
  // `name` is the stable alias, not the display label (djin-cloud#806 Task 5,
  // spec §3.1). The tooltip formatter below needs to look a series up by
  // something durable — selectedVariables[p.seriesIndex] silently mis-attributes
  // a row the moment ECharts' internal series order diverges from the array
  // order (e.g. a legend toggle). The alias is unique and stable; the
  // human-readable label is recovered for the legend via legendFormatter.
  const series = useMemo<MultiLineSeriesConfig[]>(
    () =>
      selectedVariables.map((v) => ({
        dataKey: v.alias,
        name: v.alias,
        color: v.color,
        unit: v.unit ?? undefined,
        yAxisId: v.yAxisId,
      })),
    [selectedVariables]
  );

  // Alias -> variable lookup for the tooltip (and legend) formatters below, so
  // both can resolve a series by its stable name instead of a positional index.
  const byAlias = useMemo(
    () => new Map(selectedVariables.map((v) => [v.alias, v])),
    [selectedVariables]
  );

  // Group the selected variables by the axis they were assigned to. A chip and
  // a set of bounds are produced per axis that actually has variables on it.
  const axisGroups = useMemo(() => {
    // The same placement the wrapper draws with, from the same series ids, so
    // a chip can never name a different edge than its axis is on.
    const placements = layoutYAxes(selectedVariables.map((v) => v.yAxisId));
    return AXIS_ORDER.map((id) => {
      const vars = selectedVariables.filter((v) => (v.yAxisId ?? 'left') === id);
      const units = Array.from(
        new Set(vars.map((v) => v.unit).filter((u): u is string => Boolean(u)))
      );
      const slot = placements.find((p) => p.id === id)?.index ?? 0;
      return {
        id,
        label: SLOT_LABEL[slot],
        vars,
        keys: vars.map((v) => v.alias),
        colors: vars.map((v) => v.color),
        // Mixed units on one axis have no single meaningful unit, so the
        // noise floor falls back to its relative rule and the badge omits it
        // rather than labelling the axis with one series' unit.
        unit: units.length === 1 ? units[0] : undefined,
      };
    }).filter((g) => g.vars.length > 0);
  }, [selectedVariables]);

  // Reset the zoom whenever the data underneath it changes — the old indices
  // address a different series and would mislabel the badge, and restoring a
  // stale window onto a new query would silently show the wrong slice.
  useEffect(() => {
    setZoomWindow(null);
    zoomPctRef.current = null;
  }, [queryResult, selectedVariables]);

  /** Rows currently inside the zoom window (all rows when unzoomed). */
  const visibleData = useMemo(() => {
    if (!zoomWindow) return chartData;
    const [start, end] = zoomWindow;
    return chartData.slice(start, end + 1);
  }, [chartData, zoomWindow]);

  /**
   * Bounds and reference lines per axis, derived from the FULL loaded window.
   *
   * Deliberately not a function of the zoom: for `auto` the bounds are handed
   * to ECharts as functions of the visible extent, so they re-fit themselves
   * without being rebuilt, and the nominal line marks a fixed physical value
   * that should not blink in and out as the operator pans. Keeping this memo
   * independent of zoomWindow is what lets the chart element stay memoized.
   */
  const axisBounds = useMemo(() => {
    const min: Partial<Record<YAxisId, AxisBoundValue>> = {};
    const max: Partial<Record<YAxisId, AxisBoundValue>> = {};
    const refLines: ReferenceLineConfig[] = [];

    for (const group of axisGroups) {
      const setting = yAxisScales[group.id];
      const unit = group.unit;

      if (setting.mode === 'manual') {
        if (setting.min !== undefined && setting.max !== undefined) {
          min[group.id] = setting.min;
          max[group.id] = setting.max;
        }
        continue;
      }

      // `zero` supplies no bounds: the wrapper's scale:false keeps the zero
      // baseline, which is the correct reading for power and current.
      if (setting.mode === 'zero') continue;

      min[group.id] = (extent) =>
        computeAutoBounds(extent.min, extent.max, unit)?.min ?? extent.min;
      max[group.id] = (extent) =>
        computeAutoBounds(extent.min, extent.max, unit)?.max ?? extent.max;

      // Draw the fleet nominal only when the full-window fit contains it. An
      // out-of-band reference line is noise, and a wrong one is worse than none.
      // Bounds are pinned, so a markLine can never widen the axis to reach it.
      const nominal = nominalFor(unit);
      const extent = extentOf(chartData, group.keys);
      const bounds = extent && computeAutoBounds(extent.min, extent.max, unit);
      if (nominal !== undefined && bounds && nominal >= bounds.min && nominal <= bounds.max) {
        refLines.push({
          value: nominal,
          label: `${nominal}${unit ? ` ${unit}` : ''}`,
          color: 'rgba(255,255,255,0.25)',
          yAxisId: group.id,
        });
      }
    }

    return { min, max, refLines };
  }, [axisGroups, yAxisScales, chartData]);

  /**
   * Span text per axis, derived from what is actually on screen.
   *
   * This is the honesty guard: auto-scaling changes the chart's vertical gain
   * with nothing visible to say so, and a 0.2 Hz noise band would otherwise be
   * indistinguishable from a 5 Hz excursion. It shares computeAutoBounds with
   * the axis above, so badge and axis agree by construction.
   */
  const axisSpans = useMemo(() => {
    const spans: Partial<Record<YAxisId, string>> = {};

    for (const group of axisGroups) {
      const setting = yAxisScales[group.id];
      const unit = group.unit;

      if (setting.mode === 'manual') {
        if (setting.min !== undefined && setting.max !== undefined) {
          spans[group.id] = formatAxisSpan({ min: setting.min, max: setting.max }, unit);
        }
        continue;
      }

      const extent = extentOf(visibleData, group.keys);
      if (!extent) continue;

      if (setting.mode === 'zero') {
        spans[group.id] = formatAxisSpan(
          { min: Math.min(0, extent.min), max: Math.max(0, extent.max) },
          unit
        );
        continue;
      }

      const bounds = computeAutoBounds(extent.min, extent.max, unit);
      if (bounds) spans[group.id] = formatAxisSpan(bounds, unit);
    }

    return spans;
  }, [axisGroups, yAxisScales, visibleData]);

  /**
   * Translate a settled dataZoom into row indices.
   *
   * ECharts reports percentages. Measured against echarts 6.1.0 (#806 PR1
   * probe): a wheel (inside-zoom) event carries top-level start/end AND a
   * nested batch:[{...}]; a slider drag carries top-level start/end and NO
   * batch. Both shapes expose correct top-level values, so reading batch[0]
   * when present and falling back to the params themselves otherwise is
   * correct under either event — this was previously stated as "either
   * directly or inside a batch depending on whether inside-zoom or slider
   * fired" without saying which does which; that vagueness is now measured.
   */
  const zoomTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(zoomTimer.current), []);

  const handleDataZoom = useCallback(
    (params: any) => {
      const range = Array.isArray(params?.batch) ? params.batch[0] : params;
      const startPct = typeof range?.start === 'number' ? range.start : 0;
      const endPct = typeof range?.end === 'number' ? range.end : 100;

      // Recorded immediately (not on the debounce) so a rebuild triggered
      // between the drag and the settle still restores the right window.
      zoomPctRef.current =
        startPct <= 0 && endPct >= 100 ? null : { start: startPct, end: endPct };

      clearTimeout(zoomTimer.current);
      zoomTimer.current = setTimeout(() => {
        const n = chartData.length;
        if (n === 0) return;
        if (startPct <= 0 && endPct >= 100) {
          setZoomWindow(null);
          return;
        }
        const start = Math.max(0, Math.floor((startPct / 100) * (n - 1)));
        const end = Math.min(n - 1, Math.ceil((endPct / 100) * (n - 1)));
        setZoomWindow(end > start ? [start, end] : null);
      }, ZOOM_SETTLE_MS);
    },
    [chartData.length]
  );

  const chartEvents = useMemo(() => ({ datazoom: handleDataZoom }), [handleDataZoom]);

  // Format timestamp for X-axis based on time range duration
  const formatTimestamp = useCallback(
    (ts: number) => {
      const date = new Date(Number(ts));
      const duration = timeRange.end.getTime() - timeRange.start.getTime();
      const oneDay = 24 * 60 * 60 * 1000;

      if (duration <= oneDay) {
        // Show HH:mm for <= 24h
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      } else if (duration <= 7 * oneDay) {
        // Show MM-DD HH:mm for <= 7d
        return (
          date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
          ' ' +
          date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        );
      } else {
        // Show MM-DD for > 7d
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
      }
    },
    [timeRange]
  );

  // Rich tooltip mirroring the previous ChartTooltip: timestamp header plus a
  // row per series with "deviceId - register", value (2dp), and unit.
  const tooltipFormatter = useCallback(
    (params: any) => {
      const arr = Array.isArray(params) ? params : [params];
      if (arr.length === 0) return '';

      const date = new Date(Number(arr[0].axisValue));
      const header =
        date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }) +
        ' ' +
        date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });

      const rows = arr
        .map((p: any) => {
          const v = byAlias.get(p.seriesName) ?? byAlias.get(p.dimensionNames?.[1] ?? '');
          const y = tooltipY(p);
          const value = y === null ? 'N/A' : y.toFixed(2);
          const label = v
            ? `${v.displayLabel ?? v.deviceId} - ${v.metric}`
            : String(p.seriesName ?? '');
          const unit = v?.unit
            ? ` <span style="color:rgba(255,255,255,0.5);">${escapeHtml(v.unit)}</span>`
            : '';
          return `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin-top:4px;">
            <span style="display:flex;align-items:center;gap:6px;min-width:0;">
              <span style="width:8px;height:8px;border-radius:9999px;background:${p.color};flex-shrink:0;"></span>
              <span style="color:rgba(255,255,255,0.7);font-size:12px;">${escapeHtml(label)}</span>
            </span>
            <span style="font-family:monospace;font-size:13px;color:#fff;">${value}${unit}</span>
          </div>`;
        })
        .join('');

      return `<div style="min-width:200px;">
        <div style="color:rgba(255,255,255,0.6);font-size:11px;border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:6px;margin-bottom:2px;">${header}</div>
        ${rows}
      </div>`;
    },
    [byAlias]
  );

  // The legend still shows the human-readable label even though each series'
  // `name` is now its stable alias (djin-cloud#806 Task 5, spec §3.1).
  const legendFormatter = useCallback(
    (alias: string) => byAlias.get(alias)?.displayLabel ?? alias,
    [byAlias]
  );

  // Hoisting the chart into a memoized element keeps the badge and the chart on
  // separate render paths. `notMerge` makes every rebuild a full re-render, so
  // letting a debounced badge update reach it would restart the draw (and its
  // animation) each time the operator finished a zoom drag.
  const chartElement = useMemo(
    () => (
      <EChartsMultiLineChart
        data={chartData}
        series={series}
        xAxisKey="timestamp"
        height={height - 72}
        showZoom
        connectNulls
        showGrid
        showTooltip
        showLegend
        // A historian must not draw values it never sampled: at a 0.2 Hz axis
        // span the spline visibly overshoots between points (#2795).
        smooth={false}
        animationDuration={300}
        xAxisFormatter={formatTimestamp}
        tooltipFormatter={tooltipFormatter}
        legendFormatter={legendFormatter}
        zoomStart={zoomPctRef.current?.start}
        zoomEnd={zoomPctRef.current?.end}
        yAxisMin={axisBounds.min}
        yAxisMax={axisBounds.max}
        referenceLines={axisBounds.refLines}
        onEvents={chartEvents}
      />
    ),
    [
      chartData,
      series,
      height,
      formatTimestamp,
      tooltipFormatter,
      legendFormatter,
      chartEvents,
      axisBounds,
    ]
  );


  // Empty state - no variables selected
  if (selectedVariables.length === 0) {
    return (
      <Card
        variant="default"
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-ems-text-secondary">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg mb-2">No Variables Selected</p>
          <p className="text-sm">
            Select variables from the browser to visualize data
          </p>
        </div>
      </Card>
    );
  }

  // Loading state
  if (queryLoading) {
    return (
      <Card
        variant="default"
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-ems-blue" />
          <p className="text-ems-text-secondary">Loading chart data...</p>
        </div>
      </Card>
    );
  }

  // Error state
  if (queryError) {
    return (
      <Card
        variant="default"
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="w-8 h-8 text-ems-red" />
          <p className="text-ems-red font-medium">Failed to load data</p>
          <p className="text-ems-text-secondary text-sm max-w-md">{queryError.message}</p>
          <Button
            variant="primary"
            size="sm"
            onClick={executeQuery}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  // No data yet - prompt to execute query
  if (!queryResult || chartData.length === 0) {
    return (
      <Card
        variant="default"
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-ems-text-secondary">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg mb-2">Ready to Query</p>
          <p className="text-sm mb-4">
            {selectedVariables.length} variable
            {selectedVariables.length !== 1 ? 's' : ''} selected. Execute query
            to visualize data.
          </p>
          <Button
            variant="primary"
            onClick={executeQuery}
            leftIcon={<BarChart3 className="w-4 h-4" />}
            className="mx-auto"
          >
            Execute Query
          </Button>
        </div>
      </Card>
    );
  }

  // Chart with data
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={className}
      style={{ height }}
    >
      <Card variant="default" className="flex h-full flex-col p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {axisGroups.map((group) => (
            <YAxisScaleChip
              key={group.id}
              axisLabel={group.label}
              colors={group.colors}
              spanLabel={axisSpans[group.id]}
              setting={yAxisScales[group.id]}
              onChange={(setting) => setYAxisScale(group.id, setting)}
            />
          ))}
        </div>
        <div className="min-h-0 flex-1">{chartElement}</div>
      </Card>
    </motion.div>
  );
};

export default MultiAxisChart;
